import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dropdown, Spin, Tooltip } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import Icon from "@/components/Icon";
import { useNavigate } from "react-router-dom";
import { ApiService } from "@/service/ApiService";
import { processList } from "@/constants/process-list";

/**
 * Мэдэгдлийн хонх — уншаагүй тоо + жагсаалт + процесс руу шилжих.
 *
 * ⚠ `useAuth().api`-г ЗОРИУД ХЭРЭГЛЭХГҮЙ. Хоёр шалтгаан:
 *
 *   1. Тэр нь дуудлага бүрд `showLoading(true)` → LoadingContext руу dispatch
 *      хийдэг. 60 секунд тутмын poll нь ГЛОБАЛ loading overlay-г мөнхөд
 *      анивчуулна.
 *   2. Тэр нь render болгонд шинээр үүсдэг тул `useEffect`/`useCallback`-ийн
 *      хамаарал болгож болохгүй — effect → api → dispatch → шинэ api →
 *      effect гэсэн мөнхийн давталт үүснэ. Энэ алдаа кодын санд хоёр удаа
 *      гарсан.
 *
 * Тиймээс `ApiService`-ийг ШУУД дуудна: тогтвортой, loading-д хүрэхгүй.
 *
 * ⚠ Realtime БИШ, POLL. `BROADCAST_DRIVER=log`, pusher суугаагүй, Echo
 * комментлогдсон, service worker байхгүй — түлхэх арга огт байхгүй.
 */

/** Badge-ийн poll давтамж. Badge нь төлбөр биш — 5 секунд хэрэггүй. */
const POLL_MS = 60000;

/**
 * Мэдэгдлийн төрөл — backend-ийн `event` багана (PfFlowNotify::EVENT_*).
 * `event` хоосон/үл мэдэгдэх (PUSH г.м) бол "other".
 */
const TYPE_META = {
    mentioned: { label: "Дурдсан", icon: "AtSign", tile: "bg-violet-100 text-violet-600", dot: "bg-violet-500" },
    step_changed: { label: "Алхам", icon: "ArrowRightLeft", tile: "bg-sky-100 text-sky-600", dot: "bg-sky-500" },
    status_changed: { label: "Төлөв", icon: "Flag", tile: "bg-amber-100 text-amber-600", dot: "bg-amber-500" },
    reassigned: { label: "Шилжсэн", icon: "UserCheck", tile: "bg-emerald-100 text-emerald-600", dot: "bg-emerald-500" },
    other: { label: "Бусад", icon: "Bell", tile: "bg-slate-100 text-slate-500", dot: "bg-slate-400" },
};

const typeOf = (item) => (TYPE_META[item?.event] ? item.event : "other");

const FILTERS = [
    { key: "all", label: "Бүгд" },
    { key: "mentioned", label: "Дурдсан" },
    { key: "step_changed", label: "Алхам" },
    { key: "status_changed", label: "Төлөв" },
    { key: "reassigned", label: "Шилжсэн" },
];

const NotificationBell = () => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState("all");

    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    // Хариу ирэхээс өмнө дараагийн poll гарахыг хориглоно — сүлжээ удаашрахад
    // хүсэлт давхарлахгүй (`fibaFormPayment`-ийн хэв маяг).
    const inFlight = useRef(false);

    // Unmount болсны дараа setState дуудахгүй.
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    const fetchCount = useCallback(async () => {
        if (inFlight.current || document.hidden) return;
        inFlight.current = true;
        try {
            // ApiService нь `response`-ыг аль хэдийн задалж буцаадаг
            // (`onSuccess` → `response.data.response`), тиймээс энд
            // `{count: N}` шууд ирнэ.
            const res = await ApiService(processList.notifCount, {});
            if (alive.current && res?.count !== undefined) {
                setCount(Number(res.count) || 0);
            }
        } catch (e) {
            // Хонх нь туслах элемент — алдааг toast болгохгүй, дуугүй дахин
            // оролдоно. Хэрэглэгчийн ажлыг тасалдуулах шалтгаан байхгүй.
        } finally {
            inFlight.current = false;
        }
    }, []);

    useEffect(() => {
        fetchCount();
        const timer = setInterval(fetchCount, POLL_MS);

        // Таб нуугдсан байхад poll утгагүй; харагдмагц шууд шинэчилнэ.
        const onVisible = () => { if (!document.hidden) fetchCount(); };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [fetchCount]);

    // Жагсаалтыг ЗӨВХӨН цонх нээхэд татна — интервал дээр биш.
    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            // `getGridData` нь хуудасласан бүтэц буцаана → `{data: [...], total}`.
            const res = await ApiService(processList.notifList, { page: 1, perPage: 20 });
            const data = res?.data ?? res ?? [];
            if (alive.current) setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            if (alive.current) setItems([]);
        } finally {
            if (alive.current) setLoading(false);
        }
    }, []);

    const onOpenChange = (next) => {
        setOpen(next);
        if (next) fetchList();
    };

    const markRead = async (payload) => {
        try {
            await ApiService(processList.notifMarkRead, payload);
        } catch (e) {
            // мөн чимээгүй
        }
        fetchCount();
    };

    const onItemClick = (item) => {
        setOpen(false);

        // ⚠ ШИЛЖИЛТИЙГ ХАМГИЙН ТҮРҮҮНД, ямар ч `await`-аас ӨМНӨ хийнэ.
        //
        // Урьд нь `markRead`-ийг хүлээгээд дараа нь шилжиж байсан. Тэр
        // хооронд цонх хаагдаж, дахин render болдог тул сүлжээний хариу
        // удаашрах, эсвэл дунд нь алдаа гарахад шилжилт огт хийгдэхгүй
        // үлдэх эрсдэлтэй. Уншсан болгох нь ХОЁРДОГЧ ажил — түүний
        // төлөө хэрэглэгчийн гол үйлдлийг барьцаалах шалтгаан байхгүй.
        //
        // Сервер нь ХАМААТУУЛСАН зам хадгалдаг
        // (`/si/si000301/20?processid=345&flowstepid=60`).
        if (item.url) {
            navigate(item.url);
        }

        if (!item.is_read) {
            // Хариуг хүлээхгүй — алдаа гарвал `markRead` дотроо залгина.
            markRead({ id: item.notificationid });
            setItems((prev) =>
                prev.map((x) => (x.notificationid === item.notificationid ? { ...x, is_read: 1 } : x))
            );
        }
    };

    const markAllRead = async () => {
        await markRead({ all: 1 });
        setItems((prev) => prev.map((x) => ({ ...x, is_read: 1 })));
    };

    const typeCounts = items.reduce((acc, it) => {
        if (!it.is_read) acc[typeOf(it)] = (acc[typeOf(it)] || 0) + 1;
        return acc;
    }, {});
    const visibleItems = filter === "all" ? items : items.filter((it) => typeOf(it) === filter);

    const panel = (
        <div
            style={{ width: 380, background: "var(--ant-color-bg-elevated, #fff)" }}
            className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl"
        >
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">Мэдэгдэл</span>
                    {count > 0 && (
                        <span className="rounded-full bg-red-500 px-1.5 text-[11px] font-medium leading-5 text-white">
                            {count > 99 ? "99+" : count}
                        </span>
                    )}
                </div>
                {count > 0 && (
                    <button
                        type="button"
                        onClick={markAllRead}
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[#1677ff] transition-colors hover:bg-slate-50"
                    >
                        <CheckOutlined /> Бүгдийг уншсан болгох
                    </button>
                )}
            </div>

            <div className="flex gap-1.5 overflow-x-auto border-b border-slate-100 px-3 py-2">
                {FILTERS.map((f) => {
                    const active = filter === f.key;
                    const unread = f.key === "all" ? count : typeCounts[f.key] || 0;
                    return (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setFilter(f.key)}
                            className={`flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs transition-colors ${
                                active ? "bg-[#1677ff] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                        >
                            {f.label}
                            {unread > 0 && (
                                <span className={`rounded-full px-1 text-[10px] leading-4 ${active ? "bg-white/25" : "bg-white text-slate-500"}`}>
                                    {unread}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div style={{ maxHeight: 440, overflowY: "auto" }}>
                {loading ? (
                    <div className="py-10 text-center">
                        <Spin />
                    </div>
                ) : !visibleItems.length ? (
                    <div className="flex flex-col items-center gap-2 py-12 text-slate-400">
                        <Icon name="BellOff" size={28} />
                        <span className="text-sm">Мэдэгдэл алга</span>
                    </div>
                ) : (
                    visibleItems.map((item) => {
                        const meta = TYPE_META[typeOf(item)];
                        return (
                        <div
                            key={item.notificationid}
                            onClick={() => onItemClick(item)}
                            className={`flex cursor-pointer gap-3 border-b border-slate-50 px-4 py-3 transition-colors last:border-b-0 hover:bg-slate-50 ${
                                item.is_read ? "" : "bg-sky-50/50"
                            }`}
                        >
                            <div
                                className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                                    item.is_read ? "bg-slate-100 text-slate-400" : meta.tile
                                }`}
                            >
                                <Icon name={meta.icon} size={15} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <span
                                        className={`text-[13px] leading-snug ${
                                            item.is_read ? "text-slate-600" : "font-semibold text-slate-900"
                                        }`}
                                    >
                                        {item.title}
                                    </span>
                                    {!item.is_read && (
                                        <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${meta.dot}`} />
                                    )}
                                </div>
                                {item.description && (
                                    <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">{item.description}</div>
                                )}
                                <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                                    <span className="rounded-full bg-slate-100 px-1.5 text-slate-500">{meta.label}</span>
                                    {item.created_at}
                                </div>
                            </div>
                        </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <Dropdown
            open={open}
            onOpenChange={onOpenChange}
            trigger={["click"]}
            placement="bottomRight"
            dropdownRender={() => panel}
        >
            <Tooltip title="Мэдэгдэл">
                <button
                    type="button"
                    aria-label="Мэдэгдэл"
                    className="relative flex h-8 w-8 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-black/5"
                >
                    <Icon name="Bell" size={18} />
                    {count > 0 && (
                        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
                            {count > 99 ? "99+" : count}
                        </span>
                    )}
                </button>
            </Tooltip>
        </Dropdown>
    );
};

export default NotificationBell;
